import React, { useState, useRef } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Snackbar,
  Alert,
  IconButton,
  LinearProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const DeleteConfirmationDialog = ({ open, onClose, onConfirm, pageName }) => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [deleteTimeout, setDeleteTimeout] = useState(null);
  const [progress, setProgress] = useState(100);
  const [remainingTime, setRemainingTime] = useState(60000);
  const startTimeRef = useRef(null);
  const intervalRef = useRef(null);

  // const isEmployer = companyName?.toLowerCase() === "employer";

  const startProgress = () => {
    startTimeRef.current = Date.now();
    const interval = 50;
    const decrement = 100 / (remainingTime / interval);

    intervalRef.current = setInterval(() => {
      setProgress((prev) => Math.max(0, prev - decrement));
    }, interval);
  };

  const handleConfirmDelete = () => {
    onClose();
    setSnackbarOpen(true);
    setProgress(100);
    startProgress();

    const timeout = setTimeout(() => {
      onConfirm(true);
    }, remainingTime);

    setDeleteTimeout(timeout);
  };

  const handleUndo = () => {
    if (deleteTimeout) clearTimeout(deleteTimeout);
    if (intervalRef.current) clearInterval(intervalRef.current);

    setSnackbarOpen(false);
    setProgress(100);
    setRemainingTime(60000);
    onConfirm(false);
  };

  const handleCloseSnackbar = () => {
    if (deleteTimeout) clearTimeout(deleteTimeout);
    if (intervalRef.current) clearInterval(intervalRef.current);

    setSnackbarOpen(false);
    setProgress(100);
    onConfirm(true);
    setRemainingTime(60000);
  };

  const handleMouseEnter = () => {
    if (deleteTimeout) {
      clearTimeout(deleteTimeout);
      clearInterval(intervalRef.current);
      const elapsedTime = Date.now() - startTimeRef.current;
      setRemainingTime(remainingTime - elapsedTime);
    }
  };

  const handleMouseLeave = () => {
    startTimeRef.current = Date.now();
    startProgress();
    const timeout = setTimeout(() => {
      onConfirm(true);
      setSnackbarOpen(false);
    }, remainingTime);
    setDeleteTimeout(timeout);
  };

  return (
    <>
      {/* Delete Confirmation Dialog */}
      <Dialog open={open} 
      onClose={(event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        onClose();
      }} 
      disableEscapeKeyDown
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <Divider />
        <DialogContent>
          Are you sure you want to delete this {pageName}?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onClose(true)} color="primary">
            No
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar with Progress Bar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={null}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={(event, reason) => {
          if (reason === "clickaway") {
            return;
          }
          handleCloseSnackbar();
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div style={{ width: "100%" }}>
          <Alert
            severity="warning"
            sx={{
              borderBottomLeftRadius: 0, 
              borderBottomRightRadius: 0
            }}      
            action={
              <>
                <Button color="inherit" size="small" onClick={handleUndo}>
                  UNDO
                </Button>
                <IconButton size="small" aria-label="close" color="inherit" onClick={handleCloseSnackbar}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </>
            }
          >
            Deleting{" "}{pageName}
          </Alert>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              width: "100%",
              height: 5,
              "& .MuiLinearProgress-bar": { transition: "width 0.1s linear" },
            }}
          />
        </div>
      </Snackbar>
    </>
  );
};

export default DeleteConfirmationDialog;
